import { api } from '../lib/api';

/**
 * StripeService - Serviço dedicado para integração com Stripe
 * Gerencia checkout, portal, assinaturas e faturas
 * 
 * ATUALIZADO: Suporte a planos MENSAIS e ANUAIS
 */

// Configuração de URLs
const getBaseUrl = () => window.location.origin;

export const stripeService = {
  /**
   * Cria sessão de checkout embedded do Stripe para assinar um plano.
   * Retorna um client_secret que deve ser usado pelo EmbeddedCheckoutProvider.
   * @param {string} plan - Nome do plano (basic, pro, max)
   * @param {string} billingInterval - Intervalo de cobrança (monthly, yearly)
   * @param {string[]} addons - Lookup keys de add-ons one-time (e-book, consultoria)
   *   que serão cobrados imediatamente na mesma sessão, sem trial.
   * @returns {Promise<{success: boolean, data: {session_id: string, client_secret: string}}>}
   */
  async createCheckoutSession(plan, billingInterval = 'monthly', addons = []) {
    const response = await api.post('/api/stripe/create-checkout-session', {
      plan,
      billing_interval: billingInterval,
      ui_mode: 'embedded',
      return_url: `${getBaseUrl()}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
      addons,
    });
    return response.data;
  },

  /**
   * Cria checkout one-time para compra avulsa de add-ons (sem mexer na assinatura).
   * @param {string[]} addons - lookup_keys (ex: ['ebook_instagram_vende'])
   * @returns {Promise<{success: boolean, data: {client_secret: string, session_id: string}}>}
   */
  async createAddonCheckoutSession(addons = []) {
    const response = await api.post('/api/stripe/create-addon-checkout', {
      addons,
      ui_mode: 'embedded',
      return_url: `${getBaseUrl()}/services?addon_session_id={CHECKOUT_SESSION_ID}`,
    });
    return response.data;
  },

  /**
   * Cria sessão do Portal do Cliente Stripe
   * Permite gerenciar assinatura (cancelar, trocar cartão, etc)
   * @param {string} returnUrl - URL para retornar após usar o portal
   * @returns {Promise<{success: boolean, data: {portal_url: string}}>}
   */
  async createPortalSession(returnUrl = null) {
    const response = await api.post('/api/stripe/create-portal-session', {
      return_url: returnUrl || `${getBaseUrl()}/account`
    });
    return response.data;
  },

  /**
   * Obtém status atual da assinatura e uso
   * @returns {Promise<{success: boolean, data: SubscriptionStatus}>}
   */
  async getSubscriptionStatus() {
    const response = await api.get('/api/stripe/subscription-status');
    return response.data;
  },

  /**
   * Sincroniza assinatura do usuário autenticado a partir do Stripe.
   * Rede de segurança caso o webhook não tenha processado o evento.
   * Idempotente. Não lança erros para o chamador.
   */
  async syncMySubscription() {
    try {
      const response = await api.post('/api/stripe/sync-my-subscription');
      return response.data;
    } catch (err) {
      console.warn('Sync de assinatura falhou (seguindo o fluxo):', err);
      return { success: false, data: { synced: false, reason: 'error' } };
    }
  },

  /**
   * Lista faturas do usuário
   * @returns {Promise<{success: boolean, data: {invoices: Invoice[]}}>}
   */
  async getInvoices() {
    const response = await api.get('/api/stripe/invoices');
    return response.data;
  },

  /**
   * Redireciona para o Portal do Cliente
   */
  async redirectToPortal() {
    try {
      const response = await this.createPortalSession();
      if (response.success && response.data?.portal_url) {
        window.location.href = response.data.portal_url;
      } else {
        throw new Error('Falha ao criar sessão do portal');
      }
    } catch (error) {
      console.error('Erro ao redirecionar para portal:', error);
      throw error;
    }
  },

  // ===== HELPERS =====
  helpers: {
    /**
     * Informações dos planos com preços mensais e anuais
     */
    plans: {
      free: {
        name: 'Gratuito',
        monthlyPrice: 0,
        yearlyPrice: 0,
        color: 'gray',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-700',
        borderColor: 'border-gray-300',
        credits: 2,
        images: 2,
        videos: 0
      },
      basic: {
        name: 'Básico',
        monthlyPrice: 39.90,
        yearlyPrice: 399.00,   // ~R$33,25/mês → 17% desconto
        color: 'blue',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-300',
        credits: 120,
        images: 120,
        videos: 0
      },
      pro: {
        name: 'Pro',
        monthlyPrice: 79.90,
        yearlyPrice: 790.00,   // ~R$65,83/mês → 18% desconto
        color: 'amber',
        bgColor: 'bg-amber-100',
        textColor: 'text-amber-700',
        borderColor: 'border-amber-300',
        credits: 270,
        images: 250,
        videos: 4,
        popular: true
      },
      max: {
        name: 'Max',
        monthlyPrice: 149.90,
        yearlyPrice: 1490.00,  // ~R$124,17/mês → 17% desconto
        color: 'purple',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-700',
        borderColor: 'border-purple-300',
        credits: 550,
        images: 500,
        videos: 10
      }
    },

    /**
     * Custos em créditos por tipo de geração (espelha o backend)
     */
    creditCosts: {
      image: 1,
      video: 5
    },

    /**
     * Retorna informações do plano
     * @param {string} planId - ID do plano
     */
    getPlanInfo(planId) {
      return this.plans[planId] || this.plans.free;
    },

    /**
     * Retorna preço baseado no intervalo
     * @param {string} planId - ID do plano
     * @param {string} interval - monthly ou yearly
     */
    getPrice(planId, interval = 'monthly') {
      const plan = this.plans[planId];
      if (!plan) return 0;
      return interval === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
    },

    /**
     * Retorna preço equivalente mensal (para exibir economia do anual)
     * @param {string} planId - ID do plano
     */
    getYearlyMonthlyEquivalent(planId) {
      const plan = this.plans[planId];
      if (!plan || !plan.yearlyPrice) return 0;
      return plan.yearlyPrice / 12;
    },

    /**
     * Calcula percentual de desconto do plano anual
     * @param {string} planId - ID do plano
     */
    getYearlyDiscount(planId) {
      const plan = this.plans[planId];
      if (!plan || !plan.monthlyPrice || !plan.yearlyPrice) return 0;
      const monthlyTotal = plan.monthlyPrice * 12;
      const savings = monthlyTotal - plan.yearlyPrice;
      return Math.round((savings / monthlyTotal) * 100);
    },

    /**
     * Calcula economia em R$ por ano ao escolher anual
     * @param {string} planId - ID do plano
     */
    getYearlySavings(planId) {
      const plan = this.plans[planId];
      if (!plan || !plan.monthlyPrice || !plan.yearlyPrice) return 0;
      return (plan.monthlyPrice * 12) - plan.yearlyPrice;
    },

    /**
     * Formata valor em BRL
     * @param {number} value - Valor a formatar
     */
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },

    /**
     * Formata billing interval para exibição
     * @param {string} interval - monthly ou yearly
     */
    formatBillingInterval(interval) {
      const map = {
        'monthly': 'Mensal',
        'yearly': 'Anual'
      };
      return map[interval] || 'Mensal';
    },

    /**
     * Calcula percentual de uso
     * @param {number} used - Quantidade usada
     * @param {number} limit - Limite total
     */
    calculateUsagePercentage(used, limit) {
      if (!limit || limit === 0) return 0;
      return Math.min(100, Math.round((used / limit) * 100));
    },

    /**
     * Retorna cor da barra de progresso baseado no uso
     * @param {number} percentage - Percentual de uso
     */
    getUsageColor(percentage) {
      if (percentage >= 90) return 'bg-red-500';
      if (percentage >= 70) return 'bg-amber-500';
      return 'bg-green-500';
    },

    /**
     * Formata status da assinatura para exibição
     * @param {string} status - Status da API
     */
    formatStatus(status) {
      const statusMap = {
        'active': { text: 'Ativo', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-700' },
        'trialing': { text: 'Período de Teste', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
        'past_due': { text: 'Pagamento Pendente', color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
        'canceled': { text: 'Cancelado', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-700' },
        'inactive': { text: 'Inativo', color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
        'unpaid': { text: 'Não Pago', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-700' }
      };
      return statusMap[status] || statusMap.inactive;
    },

    /**
     * Formata data para exibição
     * @param {string} dateString - Data em formato ISO
     */
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    },

    /**
     * Verifica se pode fazer upgrade
     * @param {string} currentPlan - Plano atual
     * @param {string} targetPlan - Plano alvo
     */
    canUpgrade(currentPlan, targetPlan) {
      const planOrder = ['free', 'basic', 'pro', 'max'];
      return planOrder.indexOf(targetPlan) > planOrder.indexOf(currentPlan);
    },

    /**
     * Verifica se pode fazer downgrade
     * @param {string} currentPlan - Plano atual
     * @param {string} targetPlan - Plano alvo
     */
    canDowngrade(currentPlan, targetPlan) {
      const planOrder = ['free', 'basic', 'pro', 'max'];
      return planOrder.indexOf(targetPlan) < planOrder.indexOf(currentPlan);
    }
  }
};

export default stripeService;